import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { editProduct, deleteProduct } from "../../store/thunk/addProduct";
import { editProductAction, deleteProductAction, response } from "../../store/actions/action";
import axios from "axios";
import { getBaseUrl, ConvertBytesToImage } from "../../Utils/Utils";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

const ManageProducts = ({ userId }) => {
    const dispatch = useDispatch();
    const products = useSelector((store) => store.products.data);

    const [editingProduct, setEditingProduct] = useState(null);
    const [form, setForm] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState(null);

    const openEdit = async (product) => {
        setEditingProduct(product);
        setSelectedFile(null);
        setError(null);

        // Base form from Redux store (no image, no delivery flags)
        const base = {
            name: product.name || '',
            category: product.category || '',
            subCategory: product.subCategory || '',
            description: product.description || '',
            price: product.price || '',
            mrp: product.mrp || '',
            stockQuantity: product.stockQuantity || '',
            batchNumber: product.batchNumber || '',
            expiryDate: product.expiryDate ? product.expiryDate.substring(0, 10) : '',
            requiresPrescription: product.requiresPrescription || false,
            numberOfServings: product.numberOfServings || '',
            servingSize: product.servingSize || '',
            proteinPerServing: product.proteinPerServing || '',
            vegetarianType: product.vegetarianType || '',
            freeShipping: true,
            cashOnDelivery: true,
            returns14Day: true,
            shippingCharge: '',
            codCharge: '',
        };

        // Fetch fresh product to get correct delivery flag values
        try {
            const res = await axios.get(`${getBaseUrl()}/products/${product.id}`);
            const p = res.data;
            base.freeShipping   = p.freeShipping   !== undefined ? p.freeShipping   : true;
            base.cashOnDelivery = p.cashOnDelivery !== undefined ? p.cashOnDelivery : true;
            base.returns14Day   = p.returns14Day   !== undefined ? p.returns14Day   : true;
            base.shippingCharge = p.shippingCharge != null ? String(p.shippingCharge) : '';
            base.codCharge      = p.codCharge      != null ? String(p.codCharge)      : '';
        } catch {}

        setForm(base);
    };

    const cancelEdit = () => {
        setEditingProduct(null);
        setSelectedFile(null);
        setError(null);
    };

    const onSave = async (e) => {
        e.preventDefault();
        if (!form.name || !form.price) { setError("Name and price are required."); return; }
        setIsSaving(true);
        setError(null);

        const formData = new FormData();
        formData.append('Name', form.name);
        formData.append('Category', form.category);
        formData.append('SubCategory', form.subCategory);
        formData.append('Description', form.description);
        formData.append('Price', Number(form.price));
        if (form.mrp) formData.append('MRP', Number(form.mrp));
        if (form.stockQuantity) formData.append('StockQuantity', Number(form.stockQuantity));
        if (form.batchNumber) formData.append('BatchNumber', form.batchNumber);
        if (form.expiryDate) formData.append('ExpiryDate', form.expiryDate);
        formData.append('RequiresPrescription', form.requiresPrescription);
        if (form.numberOfServings) formData.append('NumberOfServings', form.numberOfServings);
        if (form.servingSize) formData.append('ServingSize', form.servingSize);
        if (form.proteinPerServing) formData.append('ProteinPerServing', form.proteinPerServing);
        if (form.vegetarianType) formData.append('VegetarianType', form.vegetarianType);
        formData.append('FreeShipping', form.freeShipping);
        formData.append('CashOnDelivery', form.cashOnDelivery);
        formData.append('Returns14Day', form.returns14Day);
        if (!form.freeShipping && form.shippingCharge) formData.append('ShippingCharge', form.shippingCharge);
        if (!form.cashOnDelivery && form.codCharge) formData.append('CodCharge', form.codCharge);
        formData.append('UserId', userId);
        if (selectedFile) formData.append('Image', selectedFile);

        const res = await dispatch(editProduct({ id: editingProduct.id, formData }));
        setIsSaving(false);
        if (res.payload) {
            dispatch(editProductAction(res.payload));
            dispatch(response("Product updated successfully."));
            setEditingProduct(null);
        }
    };

    const onDelete = async (product) => {
        if (!window.confirm(`Delete "${product.name}"? This cannot be undone.`)) return;
        setDeletingId(product.id);
        const res = await dispatch(deleteProduct(product.id));
        setDeletingId(null);
        if (res.payload !== undefined) {
            dispatch(deleteProductAction(product.id));
            dispatch(response("Product deleted successfully."));
        }
    };

    const field = (label, key, type = 'text', required = false) => (
        <div className="mb-3 col-12 col-md-6">
            <label className="form-label">{label}{required && <span className="text-danger"> *</span>}</label>
            <input
                type={type}
                className="form-control"
                value={form[key] ?? ''}
                onChange={(e) => setForm(f => ({ ...f, [key]: type === 'checkbox' ? e.target.checked : e.target.value }))}
            />
        </div>
    );

    if (editingProduct) {
        return (
            <div className="col-lg-8 col-md-8 col-12">
                <div className="ml-0 ml-md-4">
                    <div className="d-none d-md-block mb-4">
                        <h5 className="mb-1 text-white">Edit Product</h5>
                        <p className="mb-0 text-white small">Editing: {editingProduct.name}</p>
                    </div>
                    <div className="card">
                        <div className="card-body">
                            {error && <p className="text-danger">{error}</p>}
                            <form className="row" onSubmit={onSave}>
                                {field('Name', 'name', 'text', true)}
                                {field('Category', 'category')}
                                {field('Sub Category', 'subCategory')}
                                <div className="mb-3 col-12">
                                    <label className="form-label">Description</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={form.description ?? ''}
                                        onChange={(val) => setForm(f => ({ ...f, description: val }))}
                                        modules={{ toolbar: [
                                            [{ header: [2, 3, false] }],
                                            ['bold', 'italic', 'underline'],
                                            [{ list: 'ordered' }, { list: 'bullet' }],
                                            ['clean'],
                                        ]}}
                                        style={{ background: '#fff' }}
                                    />
                                </div>
                                {field('Price (₹)', 'price', 'number', true)}
                                {field('MRP (₹)', 'mrp', 'number')}
                                {field('Stock Quantity', 'stockQuantity', 'number')}
                                {field('Batch Number', 'batchNumber')}
                                {field('Expiry Date', 'expiryDate', 'date')}
                                <div className="mb-3 col-12">
                                    <hr className="my-1" />
                                    <small className="text-muted d-block mb-2" style={{color:'#7c6f2e'}}>Nutritional Info (optional)</small>
                                    <div className="row">
                                        <div className="mb-3 col-12 col-md-6">
                                            <label className="form-label">Number of Servings</label>
                                            <input type="text" className="form-control" placeholder="e.g. 30"
                                                value={form.numberOfServings ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, numberOfServings: e.target.value }))} />
                                        </div>
                                        <div className="mb-3 col-12 col-md-6">
                                            <label className="form-label">Serving Size</label>
                                            <input type="text" className="form-control" placeholder="e.g. 5 g"
                                                value={form.servingSize ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, servingSize: e.target.value }))} />
                                        </div>
                                        <div className="mb-3 col-12 col-md-6">
                                            <label className="form-label">Protein per Serving</label>
                                            <input type="text" className="form-control" placeholder="e.g. 12 g"
                                                value={form.proteinPerServing ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, proteinPerServing: e.target.value }))} />
                                        </div>
                                        <div className="mb-3 col-12 col-md-6">
                                            <label className="form-label">Vegetarian / Non-Vegetarian</label>
                                            <select className="form-control"
                                                value={form.vegetarianType ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, vegetarianType: e.target.value }))}>
                                                <option value="">-- Select --</option>
                                                <option value="Vegetarian">Vegetarian</option>
                                                <option value="Non-Vegetarian">Non-Vegetarian</option>
                                            </select>
                                        </div>
                                    </div>
                                    <hr className="my-1" />
                                </div>
                                <div className="mb-3 col-12 col-md-6">
                                    <label className="form-label">Product Image <small className="text-muted">(leave blank to keep existing)</small></label>
                                    <input type="file" className="form-control" accept="image/*" onChange={(e) => setSelectedFile(e.target.files[0])} />
                                    <div className="d-flex gap-3 mt-2 align-items-start flex-wrap">
                                        {editingProduct?.image && !selectedFile && (
                                            <div className="text-center">
                                                <small className="text-muted d-block mb-1">Current</small>
                                                <img
                                                    src={ConvertBytesToImage(editingProduct.image)}
                                                    alt="Current"
                                                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                                                />
                                            </div>
                                        )}
                                        {selectedFile && (
                                            <div className="text-center">
                                                <small className="text-muted d-block mb-1">New</small>
                                                <img
                                                    src={URL.createObjectURL(selectedFile)}
                                                    alt="New"
                                                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 6, border: '2px solid #0d6efd' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-3 col-12">
                                    <small className="text-muted d-block mb-2">Product Options</small>
                                    <div className="form-check mb-2">
                                        <input className="form-check-input" type="checkbox" id="editRx"
                                            checked={form.requiresPrescription}
                                            onChange={(e) => setForm(f => ({ ...f, requiresPrescription: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor="editRx">Requires Prescription (Rx)</label>
                                    </div>
                                    <div className="form-check mb-1">
                                        <input className="form-check-input" type="checkbox" id="editFreeShip"
                                            checked={form.freeShipping}
                                            onChange={(e) => setForm(f => ({ ...f, freeShipping: e.target.checked, shippingCharge: e.target.checked ? '' : f.shippingCharge }))} />
                                        <label className="form-check-label" htmlFor="editFreeShip">Free Shipping</label>
                                    </div>
                                    {!form.freeShipping && (
                                        <div className="mb-2 ms-4" style={{maxWidth:220}}>
                                            <label className="form-label small mb-1">Shipping Charge (₹)</label>
                                            <input type="number" className="form-control form-control-sm" placeholder="e.g. 40" min="0"
                                                value={form.shippingCharge ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, shippingCharge: e.target.value }))} />
                                        </div>
                                    )}
                                    <div className="form-check mb-1">
                                        <input className="form-check-input" type="checkbox" id="editCOD"
                                            checked={form.cashOnDelivery}
                                            onChange={(e) => setForm(f => ({ ...f, cashOnDelivery: e.target.checked, codCharge: e.target.checked ? '' : f.codCharge }))} />
                                        <label className="form-check-label" htmlFor="editCOD">Cash on Delivery</label>
                                    </div>
                                    {!form.cashOnDelivery && (
                                        <div className="mb-2 ms-4" style={{maxWidth:220}}>
                                            <label className="form-label small mb-1">COD Charge (₹)</label>
                                            <input type="number" className="form-control form-control-sm" placeholder="e.g. 20" min="0"
                                                value={form.codCharge ?? ''}
                                                onChange={(e) => setForm(f => ({ ...f, codCharge: e.target.value }))} />
                                        </div>
                                    )}
                                    <div className="form-check">
                                        <input className="form-check-input" type="checkbox" id="editReturns"
                                            checked={form.returns14Day}
                                            onChange={(e) => setForm(f => ({ ...f, returns14Day: e.target.checked }))} />
                                        <label className="form-check-label" htmlFor="editReturns">14-Day Returns</label>
                                    </div>
                                </div>
                                <div className="col-12 d-flex gap-2 mb-3">
                                    <button className="btn btn-primary" type="submit" disabled={isSaving}>
                                        {isSaving && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>}
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                    <button className="btn btn-secondary" type="button" onClick={cancelEdit}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="col-lg-8 col-md-8 col-12">
            <div className="ml-0 ml-md-4">
                <div className="d-none d-md-block mb-4">
                    <h5 className="mb-1 text-white">Manage Products</h5>
                    <p className="mb-0 text-white small">{products.length} products in catalog</p>
                </div>
                <div className="card">
                    <div className="card-body p-0">
                        {products.length === 0 ? (
                            <p className="p-4 mb-0 text-muted">No products found.</p>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 60 }}>Image</th>
                                            <th>Name</th>
                                            <th>Category</th>
                                            <th>Price</th>
                                            <th>Stock</th>
                                            <th>Rx</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map((p) => (
                                            <tr key={p.id}>
                                                <td className="align-middle">
                                                    {p.image
                                                        ? <img src={ConvertBytesToImage(p.image)} alt={p.name} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }} />
                                                        : <div style={{ width: 44, height: 44, background: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }} />
                                                    }
                                                </td>
                                                <td className="align-middle">{p.name}</td>
                                                <td className="align-middle text-muted small">{p.category}</td>
                                                <td className="align-middle">₹{p.price}</td>
                                                <td className="align-middle">
                                                    <span className={`badge ${p.stockQuantity < 10 ? 'bg-danger' : 'bg-success'}`}>
                                                        {p.stockQuantity ?? 0}
                                                    </span>
                                                </td>
                                                <td className="align-middle">
                                                    {p.requiresPrescription ? <span className="badge bg-warning text-dark">Rx</span> : '—'}
                                                </td>
                                                <td className="align-middle">
                                                    <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEdit(p)}>
                                                        Edit
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => onDelete(p)}
                                                        disabled={deletingId === p.id}
                                                    >
                                                        {deletingId === p.id
                                                            ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                            : 'Delete'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageProducts;
